import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Building2 } from "lucide-react";

export default function SettingsPage() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data));
  }, []);

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Impostazioni aggiornate");
    } catch (e) { toast.error("Errore"); }
  };

  if (!s) return <div className="text-center py-16 text-slate-400">Caricamento…</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Configurazione</div>
          <h1 className="font-display text-3xl font-bold text-[#0F2C59]">Impostazioni aziendali</h1>
        </div>
        <Button onClick={save} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1" /> Salva
        </Button>
      </div>

      <Card className="p-6 space-y-4">
        <div className="font-display font-semibold text-[#0F2C59] flex items-center gap-2"><Building2 className="w-4 h-4" /> Dati azienda</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Ragione sociale</Label><Input value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Indirizzo</Label><Input value={s.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Telefono</Label><Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div><Label>P.IVA</Label><Input value={s.piva || ""} onChange={(e) => set("piva", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Codice fiscale</Label><Input value={s.codice_fiscale || ""} onChange={(e) => set("codice_fiscale", e.target.value)} className="mt-1.5" /></div>
          <div><Label>PEC</Label><Input value={s.pec || ""} onChange={(e) => set("pec", e.target.value)} className="mt-1.5" /></div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="font-display font-semibold text-[#0F2C59]">Predefiniti offerta</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>IVA default (%)</Label><Input type="number" value={s.iva_default ?? 22} onChange={(e) => set("iva_default", Number(e.target.value))} className="mt-1.5" /></div>
          <div><Label>Validità offerta (giorni)</Label><Input type="number" value={s.validita_default ?? 30} onChange={(e) => set("validita_default", Number(e.target.value))} className="mt-1.5" /></div>
          <div><Label>Prefisso numerazione</Label><Input value={s.numerazione_prefix || "OFF"} onChange={(e) => set("numerazione_prefix", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div>
          <Label>Condizioni commerciali default</Label>
          <Textarea rows={3} value={s.condizioni_default || ""} onChange={(e) => set("condizioni_default", e.target.value)} className="mt-1.5" />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="font-display font-semibold text-[#0F2C59]">Predefiniti contratto</div>
        <p className="text-xs text-slate-500">
          Questi valori vengono usati automaticamente nella generazione dei contratti
          (Allegato B, GDPR, foro competente ecc.) quando non specificati sulla singola offerta.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Foro competente</Label>
            <Input value={s.foro_competente || ""} onChange={(e) => set("foro_competente", e.target.value)} className="mt-1.5" data-testid="setting-foro" />
          </div>
          <div>
            <Label>Luogo di sottoscrizione</Label>
            <Input value={s.luogo_sottoscrizione || ""} onChange={(e) => set("luogo_sottoscrizione", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Modalità di pagamento</Label>
            <Input value={s.modalita_pagamento_default || ""} onChange={(e) => set("modalita_pagamento_default", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Scadenze di pagamento</Label>
            <Input value={s.scadenze_pagamento_default || ""} onChange={(e) => set("scadenze_pagamento_default", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Preavviso disdetta (giorni)</Label>
            <Input type="number" value={s.giorni_preavviso_disdetta ?? 60} onChange={(e) => set("giorni_preavviso_disdetta", Number(e.target.value))} className="mt-1.5" />
          </div>
          <div>
            <Label>Sollecito prima sospensione (giorni)</Label>
            <Input type="number" value={s.giorni_sollecito_sospensione ?? 15} onChange={(e) => set("giorni_sollecito_sospensione", Number(e.target.value))} className="mt-1.5" />
          </div>
          <div>
            <Label>Verifica elaborati (giorni)</Label>
            <Input type="number" value={s.giorni_verifica_elaborati ?? 15} onChange={(e) => set("giorni_verifica_elaborati", Number(e.target.value))} className="mt-1.5" />
          </div>
          <div>
            <Label>Tentativo composizione (giorni)</Label>
            <Input type="number" value={s.giorni_tentativo_composizione ?? 30} onChange={(e) => set("giorni_tentativo_composizione", Number(e.target.value))} className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Criterio recesso anticipato</Label>
            <Input value={s.criterio_recesso_anticipato || ""} onChange={(e) => set("criterio_recesso_anticipato", e.target.value)} className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Criterio addebito trasferte</Label>
            <Input value={s.criterio_addebito_trasferte || ""} onChange={(e) => set("criterio_addebito_trasferte", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Maggiorazione urgenza</Label>
            <Input value={s.maggiorazione_urgenza || ""} onChange={(e) => set("maggiorazione_urgenza", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Validità listino</Label>
            <Input value={s.validita_listino || ""} onChange={(e) => set("validita_listino", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Tariffa trasferte (€/km)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={s.tariffa_km ?? 0.5}
              onChange={(e) => set("tariffa_km", Number(e.target.value))}
              className="mt-1.5"
              data-testid="setting-tariffa-km"
            />
            <div className="text-xs text-slate-500 mt-1">Applicata al totale km (andata+ritorno) calcolato via Google Maps.</div>
          </div>
          <div className="md:col-span-2">
            <Label>Misure tecniche GDPR</Label>
            <Textarea rows={2} value={s.misure_tecniche_gdpr || ""} onChange={(e) => set("misure_tecniche_gdpr", e.target.value)} className="mt-1.5" />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="font-display font-semibold text-[#0F2C59]">Template email</div>
        <div><Label>Oggetto</Label><Input value={s.email_template_subject || ""} onChange={(e) => set("email_template_subject", e.target.value)} className="mt-1.5" data-testid="setting-email-subject" /></div>
        <div><Label>Testo</Label><Textarea rows={6} value={s.email_template_body || ""} onChange={(e) => set("email_template_body", e.target.value)} className="mt-1.5" data-testid="setting-email-body" /></div>
        <div><Label>Firma email</Label><Input value={s.firma_email || ""} onChange={(e) => set("firma_email", e.target.value)} className="mt-1.5" /></div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="font-display font-semibold text-[#0F2C59]">Integrazioni</div>
        <div className="text-sm text-slate-600 space-y-2">
          <div>
            <div className="font-medium">Brevo (invio email)</div>
            <div className="text-xs text-slate-500 mt-0.5">Configura <code className="bg-slate-100 px-1 rounded">BREVO_API_KEY</code>, <code className="bg-slate-100 px-1 rounded">BREVO_SENDER_EMAIL</code>, <code className="bg-slate-100 px-1 rounded">BREVO_SENDER_NAME</code>, <code className="bg-slate-100 px-1 rounded">BREVO_REPLY_TO</code> nelle variabili d'ambiente backend. Chiave ottenibile su <a className="text-[#0F2C59] underline" href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noreferrer">app.brevo.com</a>. Endpoint: POST https://api.brevo.com/v3/smtp/email.</div>
          </div>
          <div>
            <div className="font-medium">Google Maps (calcolo km trasferte)</div>
            <div className="text-xs text-slate-500 mt-0.5">Configura <code className="bg-slate-100 px-1 rounded">GOOGLE_MAPS_API_KEY</code> nelle variabili d'ambiente backend. Chiave ottenibile su <a className="text-[#0F2C59] underline" href="https://console.cloud.google.com/apis/library/distance-matrix-backend.googleapis.com" target="_blank" rel="noreferrer">Google Cloud Console</a> attivando l'API "Distance Matrix".</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
